import { useEffect, useMemo } from 'react';
import { useAuth } from 'context/auth';
import { User } from 'crono-fe-common/types/user';
import { ITemplateVariableUser } from 'crono-fe-common/types/templateVariables';
import { SequenceInstance } from 'crono-fe-common/types/sequenceInstance';
import { captureException } from 'utils/exceptionUtils';

/**
 * Owners already reported this session. The personalize list renders one
 * sequence per contact, so a single owner missing their name would otherwise
 * report once per contact — the same broken payload every time.
 */
const ownersMissingNameReported = new Set<number>();

export interface PersonalizationIdentity {
  user: User | ITemplateVariableUser | null;
  onBehalfUserId?: number;
}

/**
 * Who the template variables of a sequence must resolve as.
 *
 * A manager personalizing a sequence they don't own is writing a message that
 * will be sent as its owner, and the editor persists the *substituted* text —
 * so resolving with the manager's own name or user-based snippets would bake
 * the wrong person into the message. Everything else in the content resolves
 * from the entity (prospect, account, external values) or from the
 * subscription, and is therefore already the same for both users.
 */
const usePersonalizationIdentity = (
  sequenceInstance:
    | Pick<
        SequenceInstance,
        'owned' | 'userId' | 'userFirstName' | 'userLastName'
      >
    | null
    | undefined,
): PersonalizationIdentity => {
  const { user } = useAuth();
  const { userId, userFirstName, userLastName } = sequenceInstance ?? {};
  const isNotOwned = !!sequenceInstance && !sequenceInstance.owned;

  /**
   * With neither name the owner cannot be resolved at all, which only happens if
   * the API stopped sending them: {{USER_NAME}} then renders empty for a message
   * that will be sent as this owner. Left empty rather than filled in with the
   * caller's own name, which is the mistake this hook exists to prevent — but
   * silently empty is not something to discover from a customer, hence the
   * report. One name alone can legitimately be missing, so it is not reported.
   */
  useEffect(() => {
    if (!isNotOwned || userFirstName || userLastName) return;
    if (userId === undefined || ownersMissingNameReported.has(userId)) return;

    ownersMissingNameReported.add(userId);
    captureException(
      new Error(
        'Cannot resolve the user template variables: the sequence instance carries no owner name',
      ),
      { level: 'error', extra: { sequenceOwnerUserId: userId } },
    );
  }, [isNotOwned, userId, userFirstName, userLastName]);

  return useMemo(() => {
    if (!isNotOwned) {
      return { user: user ?? null };
    }

    return {
      user: {
        firstName: userFirstName ?? null,
        lastName: userLastName ?? null,
        company: user?.company ?? null,
      },
      onBehalfUserId: userId,
    };
  }, [isNotOwned, userId, userFirstName, userLastName, user]);
};

export default usePersonalizationIdentity;
